import { FunctionComponent, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PIPELINE_CSS, PIPELINE_HTML } from './markup';

// The drawing is laid out on a fixed canvas and scaled to its host.
const WIDE = { width: 1920, height: 800 };
const STRIP = { width: 1840, height: 752, tabs: 52 };

// Below this host width the whole pipeline is too small to read, so the host
// shows one stage at a time on a strip that pans sideways.
const STRIP_BELOW = 760;

// Where the second stage starts on the strip, in canvas pixels.
const SECOND_STAGE_X = 644;

const Host = styled.div`
  position: absolute;
  inset: 0;
`;

interface ISensorFusionPipeline {
  // Read by screen readers in place of the animated diagram.
  label: string;
}

// The animated Sensor Fusion pipeline. It is drawn inside a shadow root so its
// styles stay its own. It is part of the page rather than a framed document:
// the site's Content-Security-Policy forbids framing and inline scripts.
export const SensorFusionPipeline: FunctionComponent<ISensorFusionPipeline> = ({
  label,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    shadow.innerHTML = `<style>${PIPELINE_CSS}</style>${PIPELINE_HTML}`;

    const root = shadow.querySelector<HTMLElement>('.root');
    const stage = shadow.querySelector<HTMLElement>('#stage');
    const scroller = shadow.querySelector<HTMLElement>('#scroller');
    const sizer = shadow.querySelector<HTMLElement>('#sizer');

    if (!root || !stage || !scroller || !sizer) {
      return;
    }

    const tabs = Array.from(
      shadow.querySelectorAll<HTMLButtonElement>('.tabs [data-zone]')
    );

    const snaps = tabs.map(() => {
      const snap = document.createElement('div');
      snap.className = 'snap';
      sizer.appendChild(snap);

      return snap;
    });

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let zones = [0, 0, 0];

    // Marks the stage button nearest the strip's scroll position.
    const markStage = () => {
      const x = scroller.scrollLeft;
      let nearest = 0;

      zones.forEach((zone, i) => {
        if (Math.abs(zone - x) < Math.abs(zones[nearest] - x)) {
          nearest = i;
        }
      });

      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-pressed', String(i === nearest));
      });
    };

    // Scales the canvas to the host, as a whole or as the strip.
    const layout = () => {
      const width = host.clientWidth;
      const height = host.clientHeight;
      const strip = width < STRIP_BELOW;

      root.classList.toggle('strip', strip);

      if (!strip) {
        stage.style.transform = `scale(${width / WIDE.width})`;
        sizer.style.width = `${width}px`;
        sizer.style.height = `${height}px`;
        scroller.scrollLeft = 0;

        return;
      }

      const stripHeight = height - STRIP.tabs;
      const scale = stripHeight / STRIP.height;
      const lastZone = Math.max(0, STRIP.width - width / scale);

      stage.style.transform = `scale(${scale})`;
      sizer.style.width = `${STRIP.width * scale}px`;
      sizer.style.height = `${stripHeight}px`;

      zones = [0, Math.min(SECOND_STAGE_X, lastZone), lastZone].map(
        (x) => x * scale
      );

      snaps.forEach((snap, i) => {
        snap.style.left = `${zones[i]}px`;
        snap.style.width = `${width}px`;
      });

      markStage();
    };

    const onTabClick = (i: number) => () => {
      scroller.scrollTo({
        left: zones[i],
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    };

    const tabHandlers = tabs.map((_, i) => onTabClick(i));
    tabs.forEach((tab, i) => tab.addEventListener('click', tabHandlers[i]));
    scroller.addEventListener('scroll', markStage, { passive: true });

    // Pause and play: the SVG motion and the CSS animations together.
    const svgs = Array.from(shadow.querySelectorAll<SVGSVGElement>('svg'));
    const pauseButtons = Array.from(
      shadow.querySelectorAll<HTMLButtonElement>('#pp, .tabs .pp')
    );

    let paused = false;

    const setPaused = (next: boolean) => {
      paused = next;
      root.classList.toggle('paused', paused);

      svgs.forEach((svg) => {
        if (paused) {
          svg.pauseAnimations?.();
        } else {
          svg.unpauseAnimations?.();
        }
      });

      pauseButtons.forEach((button) => {
        button.textContent = paused ? 'Play' : 'Pause';
      });
    };

    const onPauseClick = () => setPaused(!paused);
    pauseButtons.forEach((button) =>
      button.addEventListener('click', onPauseClick)
    );

    if (reducedMotion) {
      setPaused(true);
    }

    const observer = new ResizeObserver(layout);
    observer.observe(host);
    layout();

    return () => {
      observer.disconnect();
      shadow.innerHTML = '';
    };
  }, []);

  return <Host ref={hostRef} role="img" aria-label={label} />;
};
